import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addClasses } from '../../../../utils/eventListeners';
import { accountAPI, authAPI } from '../../../../services/redwolfSdkAPI';
import { setUserProfile, UserProfile } from '../../../../store/userSlice';
import { openNotification } from '../../../../store/commonSlice';
import { notification, isMobile } from '../../../../utils/constants';
import { SocialResponse } from '../../../common/interfaces/user';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';

import Modal from '../../../common/components/Modal';
import Spinner from '../../../common/components/Spinner';
import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import { checkEmailFormat, checkNameFormat, isNumeric } from '../../../../utils/validations';
import { getLocalStorage, getToken, setLocalStorage } from '../../../../utils/localStorage';
import AccountSkeleton from '../../../common/skeletons/AccountSkeleton';
import { useLocation } from 'react-router-dom';
import { AES, enc, mode, lib } from 'crypto-js';

interface UpdateContactModal {
	isOpenContactInfoModal?: any;
	handleContactInfoModal?: any;
	paymentMethod?: boolean;
}
interface QueryParams {
	firstName: string;
	lastName: string;
	email: string;
	zipCode: string;
}

const getQueryParams = (): QueryParams => {
	const result: QueryParams = {
		firstName: '',
		lastName: '',
		email: '',
		zipCode: ''
	};

	const searchParams = new URLSearchParams(window.location.search);
	const value = searchParams.get('d');

	if (value != null) {
		const cipherParams = lib.CipherParams.create({ ciphertext: enc.Hex.parse(value) });
		const privateKey = enc.Utf8.parse('80AA3AE1FA23387744AA94AD8FAC8715');
		const initializationVector = enc.Utf8.parse('H0zUFLRQu3v603DP');
		const wordArray = AES.decrypt(cipherParams, privateKey, {
			iv: initializationVector,
			mode: mode.CBC
		});
		const params = new URLSearchParams(enc.Utf8.stringify(wordArray));
		const timestamp = params.get('ts');

		if (timestamp != null) {
			const expirationDate = new Date(timestamp);
			expirationDate.setDate(expirationDate.getDate() + 1);

			if (new Date() < expirationDate) {
				result.firstName = params.get('fn') as string;
				result.lastName = params.get('ln') as string;
				result.zipCode = params.get('zc') as string;
				result.email = params.get('ea') as string;
			}
		}
	}

	return result;
};

const ContactInformation = ({
	handleContactInfoModal,
	paymentMethod,
	isOpenContactInfoModal
}: UpdateContactModal) => {
	const location: any = useLocation();

	const queryParams = getQueryParams();
	const [isEmailDisabled, setIsEmailDisabled] = useState(false);
	const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [formErrorMessages, setFormErrorMessages] = useState({
		firstName: '',
		lastName: '',
		email: '',
		zipCode: '',
		phoneNo: ''
	});

	const dispatch = useDispatch();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const {
		account: { user },
		userProfile
	} = useSelector((state: any) => state.user);
	const reformatPhoneNumber =
		user && user.phoneNumber && user.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [requestRedwolfService] = useRequestRedwolfService();
	const [contactInformationForm, setContactInformationForm] = useState({
		firstName: queryParams.firstName,
		lastName: queryParams.lastName,
		email: queryParams.email,
		zipCode: queryParams.zipCode,
		phoneNo: ''
	});

	const autoPopulateEmail = location.state?.email;
	const socialResponse: SocialResponse = location.state?.socialResponse;
	const isSocialLogin = location.state?.isSocialLogin;
	const queryStrings = new URLSearchParams(location.search);

	const firstNameRef = useRef<HTMLInputElement>(null);
	const lastNameRef = useRef<HTMLInputElement>(null);
	const emailRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);
	const zipCodeRef = useRef<HTMLInputElement>(null);
	const accountContainerFocusRef: any = useRef<HTMLDivElement>(null);
	const token = getToken();
	const accountTabindex = !isMobile ? { tabIndex: -1 } : {};

	const transaction = Sentry.startInactiveSpan({ name: 'Contact Information' });

	useEffect(() => {
		if (isSocialLogin) {
			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_VERIFY_ACCOUNT');
			if (socialResponse) {
				setContactInformationForm({
					...contactInformationForm,
					firstName: socialResponse.firstName,
					lastName: socialResponse.lastName,
					email:
						socialResponse.email &&
						(socialResponse.email.includes('no-email-provided') || !socialResponse.email)
							? ''
							: socialResponse.email
				});

				if (socialResponse.email) {
					if (!socialResponse.email.includes('no-email-provided')) {
						setIsEmailDisabled(true);
					}
				}
			}
		} else {
			logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_AUTH', { item_title: 'create_account' });
		}

		return () => {
			window.history.replaceState(null, '');
		};
	}, []);

	useEffect(() => {
		addClasses();

		if (accountContainerFocusRef.current) {
			accountContainerFocusRef.current.focus();
		}
		if (autoPopulateEmail) {
			setContactInformationForm({ ...contactInformationForm, email: autoPopulateEmail });
		}
	}, []);

	useEffect(() => {
		if (user) {
			setContactInformationForm({
				firstName: user && user.firstName,
				lastName: user && user.lastName,
				email: user && user.email && user.email !== 'null' ? user.email : '',
				zipCode: user && user.postalCode,
				phoneNo: reformatPhoneNumber && reformatPhoneNumber
			});
		}
	}, [user]);

	const handleSubmit = (event: any) => {
		event.preventDefault();
		const isEmailValid = checkEmailFormat(contactInformationForm.email);
		const isContactFormValid = {
			firstname: false,
			lastname: false,
			email: false,
			phoneno: false,
			zipcode: false
		};
		const contactInfoErrorMessage = {
			firstName: '',
			lastName: '',
			email: '',
			phoneNo: '',
			zipCode: ''
		};
		const requiredFieldMessage = 'This is a required field.';
		const invalidEmailFormat = 'Please enter a valid email address.';
		const invalidPhoneNumber = 'Please enter a valid phone number.';
		const invalidZipCode = 'Please enter a valid ZIP Code.';

		if (!contactInformationForm.firstName) {
			contactInfoErrorMessage.firstName = requiredFieldMessage;
			isContactFormValid.firstname = true;
		}
		if (!contactInformationForm.lastName) {
			contactInfoErrorMessage.lastName = requiredFieldMessage;
			isContactFormValid.lastname = true;
		}
		if (!contactInformationForm.email) {
			contactInfoErrorMessage.email = requiredFieldMessage;
			isContactFormValid.email = true;
		}
		if (contactInformationForm.email && !isEmailValid) {
			contactInfoErrorMessage.email = invalidEmailFormat;
			isContactFormValid.email = true;
		}
		if (!contactInformationForm.zipCode) {
			contactInfoErrorMessage.zipCode = requiredFieldMessage;
			isContactFormValid.zipcode = true;
		}
		if (contactInformationForm.zipCode && contactInformationForm.zipCode.length < 5) {
			contactInfoErrorMessage.zipCode = invalidZipCode;
			isContactFormValid.zipcode = true;
		}
		if (contactInformationForm.phoneNo && contactInformationForm.phoneNo.length < 14) {
			contactInfoErrorMessage.phoneNo = invalidPhoneNumber;
			isContactFormValid.phoneno = true;
		}

		setFormErrorMessages(contactInfoErrorMessage);

		const setFocus: any = Object.entries(isContactFormValid).find(([key, isFieldValid]) => {
			return isFieldValid === true;
		});

		if (setFocus) {
			setFocusToInput(setFocus[0]);
		} else {
			callUpdateUserService();
		}
	};

	const setFocusToInput = (setFocus: any) => {
		if (setFocus === 'firstname' && firstNameRef.current) {
			firstNameRef.current.focus();
		} else if (setFocus === 'lastname' && lastNameRef.current) {
			lastNameRef.current.focus();
		} else if (setFocus === 'phoneno' && phoneRef.current) {
			phoneRef.current.focus();
		} else if (setFocus === 'zipcode' && zipCodeRef.current) {
			zipCodeRef.current.focus();
		} else if (setFocus === 'email' && emailRef.current) {
			emailRef.current.focus();
		}
	};

	const callUpdateUserService = () => {
		setIsSpinnerLoading(true);

		requestRedwolfService(authAPI.getEmailPreference(token))
			.then((response: any) => {
				setIsSpinnerLoading(false);
				if (response.data && response.data.emailOptin) {
					callUpdateUserService2(response.data.emailOptin);
				}
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const callUpdateUserService2 = (emailOptin: boolean) => {
		setIsSpinnerLoading(true);
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CONTACT_INFO_UPDATE');

		const updateUserInfo = {
			accessToken: token,
			firstName: contactInformationForm.firstName,
			lastName: contactInformationForm.lastName,
			email: contactInformationForm.email,
			emailOptin: emailOptin,
			postalCode: contactInformationForm.zipCode,
			phoneNumber: contactInformationForm.phoneNo
		};

		requestRedwolfService(accountAPI.updateUserDetails(updateUserInfo))
			.then((response: any) => {
				setIsSpinnerLoading(false);

				if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CONTACT_INFO_UPDATE_SUCCESS');

					//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login
					const responseData = response.data;
					const updatedUserProfile: UserProfile = {
						firstName: responseData.firstName,
						lastName: responseData.lastName,
						email: responseData.email,
						userId: responseData.userId,
						signInType: userProfile.signInType,
						requiresCreateUser: false,
						isUserLoggedIn: true,
						emailVerified: responseData.emailVerified,
						requiresEmailVerification: responseData.requiresEmailVerification,
						requiresPersonalization: responseData.requiresPersonalization
					};

					dispatch(setUserProfile({ userProfile: updatedUserProfile, user: responseData }));
					setIsContactInfoModalOpen(false);

					if (getLocalStorage('isRequiresPersonalization')) {
						setLocalStorage('isRequiresPersonalization', false);
					}

					if (handleContactInfoModal) {
						handleContactInfoModal();
					}

					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.contactInfoSuccess
						})
					);
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CONTACT_INFO_UPDATE_FAILURE', {
						error: response
					});
					setIsSpinnerLoading(false);

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.contactInfoError
						})
					);
					Sentry.captureException(new Error(`updateContactInformation: ${response}`));
				}

				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				showTechnicalErrorMessage();
				Sentry.captureException(err);
			});
	};

	const handleHide = () => {
		setContactInformationForm({
			firstName: user && user.firstName,
			lastName: user && user.lastName,
			email: user ? user.email : '',
			zipCode: user && user.postalCode,
			phoneNo: reformatPhoneNumber && reformatPhoneNumber
		});
		setFormErrorMessages({
			...formErrorMessages,
			firstName: '',
			lastName: '',
			email: '',
			zipCode: '',
			phoneNo: ''
		});
		setIsContactInfoModalOpen(false);

		if (handleContactInfoModal) {
			handleContactInfoModal();
		}
	};

	const handleKeyDown = (event: any) => {
		const { name, value, selectionStart, selectionEnd } = event.target;
		const RegexNumberSpace = /^[0-9\s]+$/i;
		const RegexNumber = /^[0-9]+$/i;

		if ((name === 'firstName' || name === 'lastName') && !checkNameFormat(event.key)) {
			event.preventDefault();
		} else if (
			name === 'phoneNo' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart - 1]) &&
			value[selectionStart]
		) {
			phoneRef.current &&
				phoneRef.current.setSelectionRange(selectionStart - 1, selectionStart - 1);
			event.preventDefault();
		} else if (
			name === 'phoneNo' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumberSpace.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		} else if (
			name === 'phoneNo' &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumber.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		}
	};

	const handleChange = (event: any) => {
		const { name, value } = event.target;
		const contactInformation = { ...contactInformationForm, [name]: value };
		setFormErrorMessages({ ...formErrorMessages, [name]: '' });

		if (name === 'phoneNo' && value.length < 15) {
			let formattedPhoneNumber = value.replace(/[^\d]/g, '');
			const cursorPosition = event.target.selectionStart;

			if (formattedPhoneNumber.length === 0) {
				formattedPhoneNumber = '';
			} else if (formattedPhoneNumber.length > 0 && formattedPhoneNumber.length < 4) {
				formattedPhoneNumber = `(${formattedPhoneNumber}`;
			} else if (formattedPhoneNumber.length > 3 && formattedPhoneNumber.length < 7) {
				formattedPhoneNumber = `(${formattedPhoneNumber.slice(0, 3)}) ${formattedPhoneNumber.slice(
					3,
					6
				)}`;
			} else if (formattedPhoneNumber.length >= 7) {
				formattedPhoneNumber = `(${formattedPhoneNumber.slice(0, 3)}) ${formattedPhoneNumber.slice(
					3,
					6
				)}-${formattedPhoneNumber.slice(6, 10)}`;
			}

			if (value[cursorPosition]) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				value[cursorPosition] === ')'
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				(value[cursorPosition] === '(' || value[cursorPosition] === '-')
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
				}, 0);
			}
			if (
				event.nativeEvent.inputType === 'deleteContentBackward' ||
				event.nativeEvent.inputType === 'deleteContentForward'
			) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}

			contactInformation.phoneNo = formattedPhoneNumber;
		}

		setContactInformationForm({ ...contactInformation });
	};

	const zipCodeInputChange = (event: any) => {
		const { value } = event.target;
		setFormErrorMessages({ ...formErrorMessages, zipCode: '' });

		if (!value) {
			setContactInformationForm({ ...contactInformationForm, zipCode: '' });
		}
		if (value.length < 6 && isNumeric(value)) {
			setContactInformationForm({ ...contactInformationForm, zipCode: value });
		}
	};

	if (firstNameRef.current && firstNameRef.current.value) {
		firstNameRef.current.classList.add('active');
	}
	if (lastNameRef.current && lastNameRef.current.value) {
		lastNameRef.current.classList.add('active');
	}
	if (emailRef.current && emailRef.current.value) {
		emailRef.current.classList.add('active');
	}
	if (phoneRef.current && phoneRef.current.value) {
		phoneRef.current.classList.add('active');
	}
	if (zipCodeRef.current && zipCodeRef.current.value) {
		zipCodeRef.current.classList.add('active');
	}
	if (phoneRef.current && phoneRef.current.value === '') {
		phoneRef.current.classList.remove('active');
	}

	return (
		<div className="contact-info-area" ref={accountContainerFocusRef} {...accountTabindex}>
			<div className="contact-info-area-left">
				{!paymentMethod && (
					<h2 id="edit-contact-info" className="contact-info">
						Contact Information
					</h2>
				)}
				{!paymentMethod && user && (
					<>
						<p className="acc-info-name">
							Name:
							<span className="acc-info-name-field">
								{user ? user.firstName + ' ' + user.lastName : ''}
							</span>
						</p>
						<p className="acc-info-name">
							Email:
							<span className="acc-info-name-field">
								{user && user.email && user.email !== 'null' ? user.email : ''}
							</span>
						</p>
						{reformatPhoneNumber && (
							<p className="acc-info-name">
								Phone Number:<span className="acc-info-name-field">{reformatPhoneNumber}</span>
							</p>
						)}
						{user && user.postalCode && (
							<p className="acc-info-name">
								ZIP Code:<span className="acc-info-name-field">{user.postalCode}</span>
							</p>
						)}
					</>
				)}
				{!paymentMethod && !user && <AccountSkeleton />}
			</div>

			<div className="contact-info-area-right">
				{!paymentMethod && (
					<button
						className="primary-button acc-info-edit"
						aria-describedby="edit-contact-info"
						onClick={() => setIsContactInfoModalOpen(true)}>
						Edit
					</button>
				)}

				<Modal
					containerClasses="contact-info-modal"
					show={isContactInfoModalOpen || isOpenContactInfoModal}
					onHide={handleHide}
					heading="Contact Information">
					<form className="edit-modal-form" id="user_accountinfoModal">
						<div className="form-field form-field-group">
							<div className={`field half-filed ${formErrorMessages.firstName && 'has-error'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="firstName"
										id="edit-firstname"
										aria-required="true"
										aria-describedby="fName-req"
										spellCheck="false"
										value={contactInformationForm.firstName}
										onChange={handleChange}
										onKeyDown={handleKeyDown}
										ref={firstNameRef}
									/>
									<label htmlFor="edit-firstname" className="control-label">
										First Name
									</label>

									<code id="fName-req">{formErrorMessages.firstName}</code>
								</div>
							</div>

							<div className={`field half-filed ${formErrorMessages.lastName && 'has-error'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="lastName"
										id="edit-lastname"
										aria-required="true"
										aria-describedby="lName-req"
										spellCheck="false"
										value={contactInformationForm.lastName}
										onChange={handleChange}
										onKeyDown={handleKeyDown}
										ref={lastNameRef}
									/>
									<label htmlFor="edit-lastname" className="control-label">
										Last Name
									</label>

									<code id="lName-req">{formErrorMessages.lastName}</code>
								</div>
							</div>
						</div>

						<div className="form-field form-field-group">
							<div className={`field half-filed ${formErrorMessages.phoneNo && 'has-error'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="phoneNo"
										id="edit-phone-no"
										aria-describedby="phone-no-req"
										value={contactInformationForm.phoneNo}
										onChange={handleChange}
										onKeyDown={handleKeyDown}
										maxLength={14}
										ref={phoneRef}
									/>
									<label htmlFor="edit-phone-no" className="control-label">
										Phone Number
									</label>

									<code id="phone-no-req">{formErrorMessages.phoneNo}</code>
								</div>
							</div>

							<div className={`field half-filed ${formErrorMessages.zipCode && 'has-error'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										id="edit-zip-code"
										name="zipCode"
										maxLength={5}
										aria-describedby="zipcode-req"
										aria-required="true"
										value={contactInformationForm.zipCode}
										onChange={zipCodeInputChange}
										ref={zipCodeRef}
									/>
									<label htmlFor="edit-zip-code" className="control-label">
										ZIP Code
									</label>

									<code id="zipcode-req">{formErrorMessages.zipCode}</code>
								</div>
							</div>
						</div>

						<div className={`form-field ${formErrorMessages.email && 'has-error'}`}>
							<div className="input-box form-group input-label">
								<input
									type="text"
									className="form-control disable"
									name="email"
									id="edit-email"
									aria-required="true"
									autoComplete="email"
									aria-describedby="email-required"
									aria-invalid="false"
									spellCheck="false"
									value={contactInformationForm.email}
									onChange={handleChange}
									ref={emailRef}
									readOnly={user && user.email && user.email !== 'null' ? true : false}
								/>
								<label htmlFor="edit-email" className="control-label">
									Email
								</label>

								<code id="email-required">{formErrorMessages.email}</code>
							</div>
						</div>

						<div className="form-field content-right">
							<button
								id="updateUserBtn"
								type="submit"
								onClick={handleSubmit}
								className="primary-button"
								value="SAVE">
								Save
							</button>
						</div>
					</form>
				</Modal>
			</div>

			{isSpinnerLoading && <Spinner />}
		</div>
	);
};

export default ContactInformation;
